// Context pour gérer le chargement des médias
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { MediaImportanceService, type MediaType, type MediaImportanceResult } from '../Services/MediaImportanceService';
import { LazyLoadLogger } from '../Services/LazyLoadLogger';

export interface MediaLoadState {
    isLoading: boolean;
    currentQuality: number | null;
    targetQuality: number;
    error: Error | null;
}

export interface RegisteredMedia {
    id: string;
    ref: React.RefObject<HTMLElement>;
    type: MediaType;
    loadState: MediaLoadState;
    importance: MediaImportanceResult | null;
    inQueue: boolean;
}

interface MediaLoadingContextValue {
    // State
    media: Map<string, RegisteredMedia>;
    queue: string[]; // IDs des médias en queue (triés par importance)
    isPaused: boolean;
    
    // Actions
    registerMedia: (id: string, ref: React.RefObject<HTMLElement>, type: MediaType) => void;
    unregisterMedia: (id: string) => void;
    queueUpgrade: (id: string) => void;
    updateImportance: (id: string, importance: MediaImportanceResult) => void;
    setQuality: (id: string, quality: number) => void;
    pause: () => void;
    resume: () => void;
    reorderQueue: () => void;
}

const MediaLoadingContext = createContext<MediaLoadingContextValue | null>(null);

export const MediaLoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [media, setMedia] = useState<Map<string, RegisteredMedia>>(new Map());
    const [queue, setQueue] = useState<string[]>([]);
    const [isPaused, setIsPaused] = useState(false);
    
    const mediaRef = useRef<Map<string, RegisteredMedia>>(new Map());
    const queueRef = useRef<string[]>([]);

    // Sync refs
    useEffect(() => {
        mediaRef.current = media;
        queueRef.current = queue;
    }, [media, queue]);

    /**
     * Enregistre un média
     */
    const registerMedia = useCallback((
        id: string,
        ref: React.RefObject<HTMLElement>,
        type: MediaType
    ) => {
        setMedia(prev => {
            const newMap = new Map(prev);
            newMap.set(id, {
                id,
                ref,
                type,
                loadState: {
                    isLoading: false,
                    currentQuality: null,
                    targetQuality: 95,
                    error: null
                },
                importance: null,
                inQueue: false
            });
            return newMap;
        });

        LazyLoadLogger.logMedia(`Média enregistré: ${id}`, { type });
    }, []);

    /**
     * Désenregistre un média
     */
    const unregisterMedia = useCallback((id: string) => {
        setMedia(prev => {
            const newMap = new Map(prev);
            newMap.delete(id);
            return newMap;
        });

        // Retirer de la queue
        setQueue(prev => prev.filter(mediaId => mediaId !== id));

        LazyLoadLogger.logMedia(`Média désenregistré: ${id}`);
    }, []);

    /**
     * Ajoute un média à la queue pour amélioration de qualité
     */
    const queueUpgrade = useCallback((id: string) => {
        const mediaItem = mediaRef.current.get(id);
        if (!mediaItem) {
            LazyLoadLogger.warn('media', `Média non trouvé: ${id}`);
            return;
        }

        // Si déjà en queue, ne pas ajouter
        if (mediaItem.inQueue || queueRef.current.includes(id)) {
            return;
        }

        setMedia(prev => {
            const newMap = new Map(prev);
            const item = newMap.get(id);
            if (item) {
                item.inQueue = true;
            }
            return newMap;
        });

        setQueue(prev => {
            const newQueue = [...prev, id];
            // Trier par importance
            return sortQueueByImportance(newQueue, mediaRef.current);
        });

        LazyLoadLogger.logMedia(`Média ajouté à la queue: ${id}`, {
            queueLength: queueRef.current.length + 1
        });
    }, []);

    /**
     * Met à jour le score d'importance d'un média
     */
    const updateImportance = useCallback((id: string, importance: MediaImportanceResult) => {
        setMedia(prev => {
            const newMap = new Map(prev);
            const item = newMap.get(id);
            if (item) {
                item.importance = importance;
            }
            return newMap;
        });

        LazyLoadLogger.logMedia(`Importance mise à jour: ${id}`, {
            score: importance.score,
            visibility: importance.visibilityScore,
            size: importance.sizeScore,
            position: importance.positionScore
        });

        // Si en queue, réordonner
        if (queueRef.current.includes(id)) {
            setQueue(prev => sortQueueByImportance(prev, mediaRef.current));
        }
    }, []);

    /**
     * Définit la qualité actuelle d'un média
     */
    const setQuality = useCallback((id: string, quality: number) => {
        setMedia(prev => {
            const newMap = new Map(prev);
            const item = newMap.get(id);
            if (item) {
                item.loadState.currentQuality = quality;
            }
            return newMap;
        });
    }, []);

    /**
     * Met en pause le traitement de la queue
     */
    const pause = useCallback(() => {
        setIsPaused(true);
        LazyLoadLogger.logMedia('Queue de médias mise en pause');
    }, []);

    /**
     * Reprend le traitement de la queue
     */
    const resume = useCallback(() => {
        setIsPaused(false);
        LazyLoadLogger.logMedia('Queue de médias reprise');
    }, []);

    /**
     * Réordonne la queue selon l'importance
     */
    const reorderQueue = useCallback(() => {
        setQueue(prev => sortQueueByImportance(prev, mediaRef.current));
        LazyLoadLogger.logMedia('Queue réordonnée');
    }, []);

    const value: MediaLoadingContextValue = {
        media,
        queue,
        isPaused,
        registerMedia,
        unregisterMedia,
        queueUpgrade,
        updateImportance,
        setQuality,
        pause,
        resume,
        reorderQueue
    };

    return (
        <MediaLoadingContext.Provider value={value}>
            {children}
        </MediaLoadingContext.Provider>
    );
};

/**
 * Hook pour utiliser le context
 */
export function useMediaLoading() {
    const context = useContext(MediaLoadingContext);
    if (!context) {
        throw new Error('useMediaLoading must be used within MediaLoadingProvider');
    }
    return context;
}

/**
 * Trie la queue par importance (score le plus élevé en premier)
 */
function sortQueueByImportance(
    queue: string[],
    media: Map<string, RegisteredMedia>
): string[] {
    return [...queue].sort((a, b) => {
        const mediaA = media.get(a);
        const mediaB = media.get(b);
        
        const scoreA = mediaA?.importance?.score || 0;
        const scoreB = mediaB?.importance?.score || 0;
        
        return scoreB - scoreA; // Plus haut score en premier
    });
}



